import { Component, ViewChild, OnInit, Input } from '@angular/core'
import {
  ActionButtonsColumnRendererComponent,
  BooleanFilterComponent,
  DateTimeColumnRendererComponent,
  GridWrapperComponent,
  LookupFilterComponent,
  Option
} from 'common-components'
import { ColDef } from 'ag-grid-community'
import { Subject } from 'rxjs'
import { ToastrService } from 'ngx-toastr'
import {
  IPlan,
  IProvider,
  IProviderCoverage,
  PayerODataService,
  PlanODataService,
  ProviderCoveragesODataService
} from '@exchange/app/domain'
import { UtilService } from '@exchange/app/services'
import { EpisodeTypeODataService } from '@exchange/app/domain/system/episodeTypes'
import { ActivatedRoute } from '@angular/router'
import { PlanRendererComponent } from './plan.renderer.component'
import { EpisodeTypeRendererComponent } from './episode-type.renderer.component'

@Component({
  selector: 'app-providers-edit-coverages',
  templateUrl: './providers-edit-coverages.component.html',
  standalone: false
})
export class ProvidersEditCoveragesComponent implements OnInit {
  @ViewChild('grid', { static: true }) grid: GridWrapperComponent

  private _modelSubject: Subject<IProvider> = new Subject<IProvider>()

  private _model: IProvider
  private providerId: number = null
  private tabInitialized = false
  @Input()
  set model(value: IProvider) {
    this._model = value
    this._modelSubject.next(this._model)
  }
  get model(): IProvider {
    return this._model
  }

  providerCoverageDelete: IProviderCoverage = null
  providerCoverage: IProviderCoverage = null
  errorMessage = ''
  payers: Option[] = []
  plans: IPlan[] = []
  payerIdsWithPlans: number[] = []
  plansOptions: Option[] = []
  episodeTypes: Option[] = []
  ianaTimeZone: string = null

  busy: any = null

  constructor(
    private odata: ProviderCoveragesODataService,
    private payerService: PayerODataService,
    private planService: PlanODataService,
    private episodeTypeService: EpisodeTypeODataService,
    public util: UtilService,
    private toastr: ToastrService,
    private route: ActivatedRoute
  ) {
    this.route.params.subscribe(params => {
      this.providerId = +params['id']
    })
  }

  ngOnInit() {
    this.initPlans()
    this.initPayers()
    this.initEpisodeTypesList()
    this.initializeGrid()

    this._modelSubject.subscribe(model => {
      this.ianaTimeZone = model.IanaTimeZone
      const columnDefs = this.grid.gridOptions.columnDefs
      columnDefs
        .filter(
          columnDef => columnDef.headerName === 'Start Date' || columnDef.headerName === 'End Date'
        )
        .forEach(columnDef => {
          const columnIndex = columnDefs.findIndex(x => x.headerName === columnDef.headerName)
          const existingColumnDef = columnDefs.splice(columnIndex, 1)[0]
          const newColumnDef = {
            ...existingColumnDef,
            refData: { IanaTimeZone: this.ianaTimeZone, dateFormat: 'L' }
          }
          columnDefs.splice(columnIndex, 0, newColumnDef)
        })
      this.grid.api.setGridOption('columnDefs', columnDefs)
    })
  }

  initializeGrid(): void {
    this.grid.gridOptions.context = {
      canEdit: true,
      canDelete: false,
      plansList: this.plansOptions,
      episodeTypesList: this.episodeTypes,
      plans: this.plans,
      payersList: this.payers,
      editHandler: this.editClicked.bind(this)
    }
    this.grid.frameworkComponents = {
      lookupFilter: LookupFilterComponent,
      boolFilter: BooleanFilterComponent
    }
    const columnDefs: ColDef[] = [
      {
        headerName: '',
        field: 'Id',
        width: 80,
        cellRenderer: ActionButtonsColumnRendererComponent
      },
      {
        headerName: 'Payer Name',
        field: 'PayerId',
        width: 200,
        filter: 'lookupFilter',
        cellRenderer: GridWrapperComponent.LookupFieldRenderer(this.payers)
      },
      {
        headerName: 'Plan(s)',
        field: 'PlanIds',
        width: 500,
        filter: 'lookupFilter',
        cellRenderer: PlanRendererComponent
      },
      {
        headerName: 'Episode Type(s)',
        field: 'EpisodeTypeIds',
        width: 300,
        filter: 'lookupFilter',
        cellRenderer: EpisodeTypeRendererComponent
      },
      {
        headerName: 'Start Date',
        field: 'StartDate',
        width: 100,
        filter: 'agDateColumnFilter',
        cellRenderer: DateTimeColumnRendererComponent,
        filterParams: { newRowsAction: 'keep' }
      },
      {
        headerName: 'End Date',
        field: 'EndDate',
        width: 100,
        filter: 'agDateColumnFilter',
        cellRenderer: DateTimeColumnRendererComponent,
        filterParams: { newRowsAction: 'keep' }
      }
    ]

    this.grid.gridOptions.columnDefs = columnDefs
    this.grid.defaultFilter = `ProviderId eq ${this.providerId}`
    this.grid.odata = this.odata
  }

  initEpisodeTypesList(): void {
    this.episodeTypeService
      .query()
      .orderBy('Name')
      .exec()
      .subscribe(response => {
        response.data.forEach(x => this.episodeTypes.push(new Option(x.Id, x.Name, x)))

        this.grid.setLookupFilterOptions('EpisodeTypeIds', this.episodeTypes)
        this.grid.refreshView()
      })
  }

  initPlans(): void {
    this.planService
      .query()
      .exec()
      .subscribe(data => {
        data.data.forEach(x => {
          this.plans.push(x)
          this.plansOptions.push(new Option(x.Id, x.Name, x))
        })
        this.grid.setLookupFilterOptions('PlanIds', this.plansOptions)
        this.grid.refreshView()
      })
  }

  initPayers(): void {
    this.payerService
      .query()
      .orderBy('Name')
      .exec()
      .subscribe(data => {
        data.data.forEach(x => this.payers.push(new Option(x.Id, x.Name, x)))
        this.grid.setLookupFilterOptions('PayerId', this.payers)
        this.grid.refreshView()
      })
  }

  showSuccess(): void {
    this.busy = null
    this.providerCoverage = null
    this.grid.getData()
  }

  showError(response): void {
    this.busy = null
    const msg = this.util.getErrorString(response)

    this.toastr.error(msg)
  }

  addEntity(): void {
    this.initializeCoverageEntity(() => {
      this.providerCoverage = {
        Id: 0,
        ProviderId: this.providerId,
        PlanIds: [],
        EpisodeTypeIds: []
      } as IProviderCoverage
    })
  }

  editClicked(data: any): void {
    this.initializeCoverageEntity(() => {
      this.providerCoverage = data as IProviderCoverage
    })
  }

  cancel(): void {
    this.providerCoverage = null
  }

  initializeCoverageEntity(fn: () => void): void {
    fn()
  }

  autoSizeGridColumns(): void {
    if (this.grid?.data?.length && !this.tabInitialized) {
      this.grid.api.sizeColumnsToFit()
      this.tabInitialized = true
    }
  }
}
